import React, { useEffect, useState } from 'react';

// Styles
import styles from './ZonesAdmin.module.css';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import useHttp from '../../../hooks/useHttp';

// Components
import ButtonsZone from '../ButtonsZone/ButtonsZone';
import ZonesSelector from '../ZonesSelector/ZonesSelector';

// Interfaces
import { ProfileZoneTown, Zone } from '../../../interfaces/Zone';
import { Profile } from '../../../interfaces/Profile';
import { Bed, ITown } from '../../../interfaces/Patient';
import { ActionReducerZoneAdministration, StateZoneAdministration } from '../../../interfaces/Administration';
import CurrentZoneItems from '../CurrentZoneItems/CurrentZoneItems';


const ZonesAdmin: React.FC<{
    profile: Profile | null
    items: (Bed | ITown)[]
    selectedItems: StateZoneAdministration
    handleDeletedZone: (zoneID: Zone['id']) => void
    updateBedsZoneID: (bedsZones: { id: string; zone_id: number | null; }[]) => void
    handleDisableAssociation: (val: boolean) => void
    dispatchSelectedItems: React.Dispatch<ActionReducerZoneAdministration>
}> = ({ profile, items, selectedItems, updateBedsZoneID, handleDeletedZone, handleDisableAssociation, dispatchSelectedItems }) => {

    const { sendRequest: getProfileZoneTown } = useHttp<ProfileZoneTown[]>();

    const [currentZone, setCurrentZone] = useState<Zone | null>(null);

    const [profileTowns, setProfileTowns] = useState<ProfileZoneTown[]>([]);

    const { sendRequest } = useHttp();

    const profileID = profile?.id;
    const zoneByBed = profile?.zone_by_bed;

    useEffect(() => {
        if (zoneByBed !== false || profileID === undefined) return;
        getProfileZoneTown({
            url: getEnvironmentApiUrl() + 'profile_zone_town/all',
            method: 'POST',
            body: { filter: { profile_id: profileID }, params: {} }
        }, (data) => setProfileTowns(data));
    }, [getProfileZoneTown, profileID, zoneByBed]);



    const updateTownsZone = (profileZoneTowns: ProfileZoneTown[], action: 'ADD' | 'DELETE') => {
        if (action === 'ADD') setProfileTowns(oldProfilesZoneTown => {
            const elementsToAdd = profileZoneTowns.filter(pzt => !oldProfilesZoneTown.some(oldPzt =>
                oldPzt.profile_id === pzt.profile_id &&
                oldPzt.town_id === pzt.town_id &&
                oldPzt.zone_id === pzt.zone_id)
            );
            return [...oldProfilesZoneTown, ...elementsToAdd];
        });
        if (action === 'DELETE') setProfileTowns(oldProfilesZoneTown => oldProfilesZoneTown.filter(oldPzt =>
            !profileZoneTowns.some(pzt =>
                oldPzt.profile_id === pzt.profile_id &&
                oldPzt.town_id === pzt.town_id &&
                oldPzt.zone_id === pzt.zone_id)
        ));
    };


    const handleClickAdd = () => {
        if (currentZone === null || zoneByBed === null || zoneByBed === undefined || profileID === undefined) return;
        if (zoneByBed === true && selectedItems.addBedIDs.length > 0) {
            const bedsZones = selectedItems.addBedIDs.map(id => ({ id, zone_id: currentZone.id }));
            sendRequest({
                url: getEnvironmentApiUrl() + 'bed/multiple',
                method: 'PUT',
                body: bedsZones
            }, () => updateBedsZoneID(bedsZones));
        }
        if (zoneByBed === false && selectedItems.addTownIDs.length > 0) {
            const profileZoneTowns: ProfileZoneTown[] = selectedItems.addTownIDs.map(id =>
                ({ town_id: id, zone_id: currentZone.id, profile_id: profileID }));
            sendRequest({
                url: getEnvironmentApiUrl() + 'profile_zone_town/multiple',
                method: 'POST',
                body: { profile_zone_towns: profileZoneTowns }
            }, () => updateTownsZone(profileZoneTowns, 'ADD'));
        }
        dispatchSelectedItems({ type: 'RESET_ADD' });
    };

    const handleClickRemove = () => {
        if (currentZone === null || profileID === undefined) return;
        if (zoneByBed === true && selectedItems.removeBedIDs.length > 0) {
            const bedsZones = selectedItems.removeBedIDs.map(id => ({ id, zone_id: null }));
            sendRequest({
                url: getEnvironmentApiUrl() + 'bed/multiple',
                method: 'PUT',
                body: bedsZones
            }, () => updateBedsZoneID(bedsZones));
        }
        if (zoneByBed === false && selectedItems.removeTownIDs.length > 0) {
            const profileZoneTowns = selectedItems.removeTownIDs.map(id =>
                ({ town_id: id, zone_id: currentZone.id, profile_id: profileID }));
            sendRequest({
                url: getEnvironmentApiUrl() + 'profile_zone_town/multiple',
                method: 'DELETE',
                body: profileZoneTowns
            }, () => updateTownsZone(profileZoneTowns, 'DELETE'));
        }
        dispatchSelectedItems({ type: 'RESET_REMOVE' });
    };

    const selectedItemsIDs = zoneByBed === null || zoneByBed === undefined ? []
        : zoneByBed ? selectedItems.removeBedIDs : selectedItems.removeTownIDs;

    const filteredItems = currentZone === null ? null : zoneByBed === true ?
        items.filter(b => b.zone_id === currentZone.id)
        : zoneByBed === false ?
            items.filter(t => profileTowns.some(pzt => pzt.zone_id === currentZone.id && pzt.town_id === t.id))
            : [];

    return (
        <>
            <div className={styles.zonesContainer}>
                {zoneByBed === undefined || zoneByBed === null || profile === null ? null
                    :
                    <>
                        <ZonesSelector
                            profileID={profile.id}
                            currentZoneID={currentZone?.id}
                            setCurrentZone={setCurrentZone}
                            dispatchSelectedItems={dispatchSelectedItems}
                            handleDeletedZone={handleDeletedZone}
                            handleDisableAssociation={handleDisableAssociation}
                        />
                        <CurrentZoneItems
                            items={filteredItems}
                            selectedItemsIDs={selectedItemsIDs}
                            dispatchSelectedItems={dispatchSelectedItems}
                            showBeds={zoneByBed}
                        />
                    </>
                }
            </div>
            <ButtonsZone
                currentZone={currentZone}
                selectedItems={selectedItems}
                handleClickAdd={handleClickAdd}
                handleClickRemove={handleClickRemove}
            />
        </>
    );
};

export default ZonesAdmin;